import { useContext, useEffect, useMemo, type PropsWithChildren } from 'react';
import { LoadingBlockFullPage } from '../../components/loading/loading-block-full-page.tsx';
import { useAppState } from '../../lib/hooks/use-app-state.ts';
import { applyLanguage } from '../../lib/i18n/i18n.ts';
import type { AppConfig } from '../../types/app-config-type.ts';
import { api } from '../../utils/api.ts';
import { AuthClient } from '../../utils/auth-client.ts';
import { AclProviderContext } from './acl-context.ts';

export function AclProvider({ children }: PropsWithChildren) {
    const { platoUser } = useAppState();

    const { data: appConfig } = api.platoUser.query.appConfig.getAppConfig.useQuery(undefined, {
        enabled: !!platoUser,
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchInterval: 5_000 * 60, // 5 minutes
    });

    const serverLanguage = platoUser.language;
    useEffect(() => {
        applyLanguage(serverLanguage);
    }, [serverLanguage]);

    const aclClient = useMemo(() => new AuthClient(appConfig), [appConfig]);

    const contextValue = useMemo(
        () =>
            appConfig
                ? {
                      authClient: aclClient,
                      appConfig,
                  }
                : undefined,
        [aclClient, appConfig],
    );

    if (!contextValue) {
        return <LoadingBlockFullPage />;
    }

    return <AclProviderContext.Provider value={contextValue}>{children}</AclProviderContext.Provider>;
}

/**
 * @deprecated Use useAppConfig instead for accessing app configuration
 */
export function useAcl(): AuthClient {
    const context = useContext(AclProviderContext);

    if (!context) {
        throw new Error('useAcl must be used within a AclProvider');
    }

    return context.authClient;
}

export function useAppConfig(): AppConfig {
    const context = useContext(AclProviderContext);

    if (!context) {
        throw new Error('useAppConfig must be used within a AclProvider');
    }

    return context.appConfig;
}
